import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconTrash } from '../../components/icons';
import MultiListSelect from '../../components/MultiListSelect';
import Pagination from '../../components/Pagination';
import RolePicker from '../../components/RolePicker';
import { labelFor, listOptions, useConfig } from '../../context/ConfigContext';
import { useConfirm } from '../../context/ConfirmContext';
import { formatDateTime } from '../../utils/format';
import vnpfLogo from '../../assets/vnpf-logo.png';

// TS port of the source (Falah) app's pages/system/UserForm.jsx.
//
// Dropped vs. source: the "Organisations" tab's Vendor/Agent/Volunteer
// pickers — those FK columns and their /vendors, /agents, /volunteers
// endpoints don't exist on VNPF's backend (Programmes/Volunteers modules
// were removed). Supplier and Customer pickers are kept — both endpoints
// exist (see routes/api.php) and both FKs are still validated by
// UserController::validated(). member_id is intentionally NOT editable
// here either, matching the backend comment: "member_id is set
// automatically when a Member is created ... no longer editable from this
// form" (see UserController::validated()).
//
interface UserRecord {
  id: number;
  full_name: string;
  first_name: string;
  last_name: string;
  email: string;
  status: 'active' | 'inactive';
  mobile: string | null;
  address: string | null;
  gender: 'male' | 'female' | '' | null;
  date_of_birth: string | null;
  roles: string[];
  supplier_id: number | null;
  customer_id: number | null;
  employer_id: number | null;
  nationalities: string[];
  languages: string[];
  identities: UserIdentityRow[];
  formatted_id: string;
  photo_url: string | null;
  two_factor_enabled: boolean;
  notify_security_emails: boolean;
}

interface UserIdentityRow {
  id: number;
  identity_type: string;
  number: string;
  issuer: string | null;
  expiry_date: string | null;
}

interface UserFormState {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  status: 'active' | 'inactive';
  mobile: string;
  address: string;
  gender: string;
  date_of_birth: string;
  roles: string[];
  supplier_id: string | number;
  customer_id: string | number;
  employer_id: string | number;
  nationalities: string[];
  languages: string[];
}

const emptyForm: UserFormState = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  status: 'active',
  mobile: '',
  address: '',
  gender: '',
  date_of_birth: '',
  roles: [],
  supplier_id: '',
  customer_id: '',
  employer_id: '',
  nationalities: [],
  languages: [],
};

const TABS = [
  'details',
  'organisations',
  'roles',
  'nationalities',
  'languages',
  'identities',
  'address',
  'password',
  'security',
  'notifications',
  'login-history',
] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  details: 'Details',
  organisations: 'Roles',
  roles: 'Permissions',
  nationalities: 'Nationalities',
  languages: 'Languages',
  identities: 'Identities',
  address: 'Address',
  password: 'Password',
  security: 'Security',
  notifications: 'Notifications',
  'login-history': 'Login History',
};

// Tabs that submit through the single main form/save button below (all
// operate on the general user-update payload); the rest are standalone
// sections with their own dedicated action, mirroring Account.tsx.
const RECORD_TABS: Tab[] = ['details', 'organisations', 'roles', 'nationalities', 'languages', 'address'];

// Which tab a validation error field belongs to, so a failed save can jump
// straight to it instead of leaving the error hidden on another tab.
const FIELD_TAB: Record<string, Tab> = {
  first_name: 'details',
  last_name: 'details',
  email: 'details',
  password: 'details',
  status: 'details',
  mobile: 'details',
  address: 'details',
  gender: 'details',
  date_of_birth: 'details',
  supplier_id: 'organisations',
  customer_id: 'organisations',
  employer_id: 'organisations',
  roles: 'roles',
  nationalities: 'nationalities',
  languages: 'languages',
};

const emptyIdentityForm = { identity_type: '', number: '', issuer: '', expiry_date: '' };

interface OrgOption {
  id: number;
  business_name?: string;
  name?: string;
}

interface Session {
  id: number;
  name: string;
  created_at: string;
  last_used_at: string | null;
}

interface HistoryPage {
  data: { id: number; created_at: string; ip_address: string | null; user_agent: string | null }[];
  current_page: number;
  last_page: number;
  total: number;
}

type FormErrors = Record<string, string[]>;

export function UserForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lists } = useConfig();
  const confirmDelete = useConfirm();
  const isNew = !id;
  const [tab, setTab] = useState<Tab>('details');
  const [form, setForm] = useState<UserFormState>(emptyForm);
  const [record, setRecord] = useState<UserRecord | null>(null);
  const [suppliers, setSuppliers] = useState<OrgOption[]>([]);
  const [customers, setCustomers] = useState<OrgOption[]>([]);
  const [employers, setEmployers] = useState<OrgOption[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loaded, setLoaded] = useState(isNew);
  const [photoError, setPhotoError] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [saving, setSaving] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ password: '', password_confirmation: '' });
  const [passwordErrors, setPasswordErrors] = useState<FormErrors>({});
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);

  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [disabling2fa, setDisabling2fa] = useState(false);

  const [notifySaving, setNotifySaving] = useState(false);

  const [history, setHistory] = useState<HistoryPage | null>(null);
  const [historyPage, setHistoryPage] = useState(1);

  const [identityForm, setIdentityForm] = useState(emptyIdentityForm);
  const [identityErrors, setIdentityErrors] = useState<FormErrors>({});

  useEffect(() => {
    // Best-effort: an admin with users.manage but not procurement/revenue
    // view permissions would 403 on one of these — that's fine, the
    // Organisations tab just shows empty pickers in that case.
    api
      .get<{ data: OrgOption[] }>('/suppliers', { params: { per_page: 100 } })
      .then((res) => setSuppliers(res.data.data))
      .catch(() => setSuppliers([]));
    api
      .get<{ data: OrgOption[] }>('/customers', { params: { per_page: 100 } })
      .then((res) => setCustomers(res.data.data))
      .catch(() => setCustomers([]));
    api
      .get<{ data: OrgOption[] }>('/employers', { params: { per_page: 100 } })
      .then((res) => setEmployers(res.data.data))
      .catch(() => setEmployers([]));
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.get<UserRecord>(`/users/${id}`).then((res) => {
      setForm({
        first_name: res.data.first_name,
        last_name: res.data.last_name,
        email: res.data.email,
        password: '',
        status: res.data.status,
        mobile: res.data.mobile ?? '',
        address: res.data.address ?? '',
        gender: res.data.gender ?? '',
        date_of_birth: res.data.date_of_birth ?? '',
        roles: res.data.roles,
        supplier_id: res.data.supplier_id ?? '',
        customer_id: res.data.customer_id ?? '',
        employer_id: res.data.employer_id ?? '',
        nationalities: res.data.nationalities ?? [],
        languages: res.data.languages ?? [],
      });
      setRecord(res.data);
      setLoaded(true);
    });
  }, [id, isNew]);

  const addIdentity = async (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      const res = await api.post<UserIdentityRow[]>(`/users/${id}/identities`, identityForm);
      setRecord((r) => (r ? { ...r, identities: res.data } : r));
      setIdentityForm(emptyIdentityForm);
      setIdentityErrors({});
    } catch (err) {
      setIdentityErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removeIdentity = async (row: UserIdentityRow) => {
    if (!(await confirmDelete('Remove this identity document?'))) return;
    const res = await api.delete<UserIdentityRow[]>(`/user-identities/${row.id}`);
    setRecord((r) => (r ? { ...r, identities: res.data } : r));
  };

  useEffect(() => {
    if (tab === 'security' && !isNew && !sessions) {
      api.get<Session[]>(`/users/${id}/sessions`).then((res) => setSessions(res.data));
    }
  }, [tab, sessions, id, isNew]);

  useEffect(() => {
    if (tab === 'login-history' && !isNew) {
      api
        .get<HistoryPage>(`/users/${id}/login-history`, { params: { page: historyPage } })
        .then((res) => setHistory(res.data));
    }
  }, [tab, historyPage, id, isNew]);

  const set =
    (key: keyof UserFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const pickPhoto = () => fileInputRef.current?.click();

  const onPhotoSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setPhotoError('');
    const data = new FormData();
    data.append('photo', file);
    setUploadingPhoto(true);
    try {
      const res = await api.post<UserRecord>(`/users/${id}/photo`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRecord(res.data);
    } catch (err) {
      setPhotoError(err instanceof ApiError ? err.fieldError('photo') ?? err.message : 'Upload failed.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = async () => {
    if (!(await confirmDelete('Remove this photo?', { confirmLabel: 'Remove' }))) return;
    setUploadingPhoto(true);
    try {
      const res = await api.delete<UserRecord>(`/users/${id}/photo`);
      setRecord(res.data);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload: Record<string, unknown> = {
      ...form,
      mobile: form.mobile || null,
      address: form.address || null,
      gender: form.gender || null,
      date_of_birth: form.date_of_birth || null,
      supplier_id: form.roles.includes('supplier') ? form.supplier_id || null : null,
      customer_id: form.roles.includes('customer') ? form.customer_id || null : null,
      employer_id: form.roles.includes('employer') ? form.employer_id || null : null,
    };
    if (!isNew && !payload.password) delete payload.password;
    try {
      if (isNew) await api.post('/users', payload);
      else await api.put(`/users/${id}`, payload);
      navigate('/users');
    } catch (err) {
      const fieldErrors: FormErrors =
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] };
      setErrors(fieldErrors);
      const firstField = Object.keys(fieldErrors).find((k) => k !== 'general');
      if (firstField && FIELD_TAB[firstField]) setTab(FIELD_TAB[firstField]);
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (passwordSaving) return;
    setPasswordSaving(true);
    setPasswordSaved(false);
    try {
      await api.put(`/users/${id}/password`, passwordForm);
      setPasswordForm({ password: '', password_confirmation: '' });
      setPasswordErrors({});
      setPasswordSaved(true);
    } catch (err) {
      setPasswordErrors(err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] });
    } finally {
      setPasswordSaving(false);
    }
  };

  const revokeSession = async (session: Session) => {
    if (!(await confirmDelete('Sign this user out of this session?', { confirmLabel: 'Sign out' }))) return;
    setRevoking(true);
    try {
      await api.delete(`/users/${id}/sessions/${session.id}`);
      const res = await api.get<Session[]>(`/users/${id}/sessions`);
      setSessions(res.data);
    } finally {
      setRevoking(false);
    }
  };

  const revokeAllSessions = async () => {
    if (!(await confirmDelete('Sign this user out of all sessions?', { confirmLabel: 'Sign out all' }))) return;
    setRevoking(true);
    try {
      await api.post(`/users/${id}/sessions/revoke-all`);
      const res = await api.get<Session[]>(`/users/${id}/sessions`);
      setSessions(res.data);
    } finally {
      setRevoking(false);
    }
  };

  const disableTwoFactor = async () => {
    if (!(await confirmDelete('Disable two-factor authentication for this user?', { confirmLabel: 'Disable' }))) return;
    setDisabling2fa(true);
    try {
      await api.post(`/users/${id}/two-factor/disable`);
      const res = await api.get<UserRecord>(`/users/${id}`);
      setRecord(res.data);
    } finally {
      setDisabling2fa(false);
    }
  };

  const toggleNotify = async (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setNotifySaving(true);
    try {
      const res = await api.put<UserRecord>(`/users/${id}/notifications`, { notify_security_emails: checked });
      setRecord(res.data);
    } finally {
      setNotifySaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <>
      <header className="page-header">
        <h1>{isNew ? 'Add user' : 'Edit user'}</h1>
      </header>

      <div className="toolbar">
        <div className="tab-group">
          {TABS.map((t) => (
            <button key={t} type="button" className={tab === t ? 'tab active' : 'tab'} onClick={() => setTab(t)}>
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        {RECORD_TABS.includes(tab) && (
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}

            {tab === 'details' && (
              <>
                {!isNew && (
                  <div className="span-2 icon-upload">
                    <img src={record?.photo_url ?? vnpfLogo} alt="" className="icon-upload-preview" />
                    <div className="icon-upload-actions">
                      <button type="button" className="btn btn-ghost" onClick={pickPhoto} disabled={uploadingPhoto}>
                        {uploadingPhoto ? 'Uploading…' : 'Upload photo'}
                      </button>
                      {record?.photo_url && (
                        <button
                          type="button"
                          className="btn btn-danger-ghost"
                          onClick={removePhoto}
                          disabled={uploadingPhoto}
                        >
                          Remove photo
                        </button>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={onPhotoSelected}
                      hidden
                    />
                    {photoError && <div className="alert alert-error">{photoError}</div>}
                  </div>
                )}
                {isNew && <p className="span-2 muted">Save the user first, then you can add a photo.</p>}
                {!isNew && record?.formatted_id && (
                  <p className="span-2 muted">User ID: {record.formatted_id} (read-only)</p>
                )}

                <label>
                  First name *
                  <input value={form.first_name} onChange={set('first_name')} required />
                  {errors.first_name && <small className="error">{errors.first_name[0]}</small>}
                </label>
                <label>
                  Last name *
                  <input value={form.last_name} onChange={set('last_name')} required />
                  {errors.last_name && <small className="error">{errors.last_name[0]}</small>}
                </label>
                <label className="span-2">
                  Email *
                  <input type="email" value={form.email} onChange={set('email')} required />
                  {errors.email && <small className="error">{errors.email[0]}</small>}
                </label>
                <label>
                  {isNew ? 'Password *' : 'New password'}
                  <input
                    type="password"
                    value={form.password}
                    onChange={set('password')}
                    placeholder={isNew ? '' : 'Leave blank to keep current'}
                    required={isNew}
                  />
                  {errors.password && <small className="error">{errors.password[0]}</small>}
                </label>
                <label>
                  Status
                  <select value={form.status} onChange={set('status')}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {errors.status && <small className="error">{errors.status[0]}</small>}
                </label>
                <label className="span-2">
                  Mobile
                  <input value={form.mobile} onChange={set('mobile')} maxLength={20} />
                  {errors.mobile && <small className="error">{errors.mobile[0]}</small>}
                </label>
                <label className="span-2">
                  Address
                  <input value={form.address} onChange={set('address')} />
                  {errors.address && <small className="error">{errors.address[0]}</small>}
                </label>
                <label>
                  Gender
                  <select value={form.gender} onChange={set('gender')}>
                    <option value="">—</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                  {errors.gender && <small className="error">{errors.gender[0]}</small>}
                </label>
                <label>
                  Date of birth
                  <input type="date" value={form.date_of_birth} onChange={set('date_of_birth')} />
                  {errors.date_of_birth && <small className="error">{errors.date_of_birth[0]}</small>}
                </label>
              </>
            )}

            {tab === 'organisations' && (
              <div className="span-2">
                <RolePicker
                  value={form.roles}
                  onChange={(roles) => setForm({ ...form, roles })}
                  variant="roles"
                  inlineControls={{
                    supplier: (
                      <>
                        <select
                          value={form.supplier_id}
                          onChange={set('supplier_id')}
                          disabled={!form.roles.includes('supplier')}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">None</option>
                          {suppliers.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.business_name}
                            </option>
                          ))}
                        </select>
                        {errors.supplier_id && <small className="error">{errors.supplier_id[0]}</small>}
                      </>
                    ),
                    customer: (
                      <>
                        <select
                          value={form.customer_id}
                          onChange={set('customer_id')}
                          disabled={!form.roles.includes('customer')}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">None</option>
                          {customers.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        {errors.customer_id && <small className="error">{errors.customer_id[0]}</small>}
                      </>
                    ),
                    employer: (
                      <>
                        <select
                          value={form.employer_id}
                          onChange={set('employer_id')}
                          disabled={!form.roles.includes('employer')}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">None</option>
                          {employers.map((e) => (
                            <option key={e.id} value={e.id}>
                              {e.name}
                            </option>
                          ))}
                        </select>
                        {errors.employer_id && <small className="error">{errors.employer_id[0]}</small>}
                      </>
                    ),
                  }}
                />
              </div>
            )}

            {tab === 'roles' && (
              <div className="span-2">
                <RolePicker value={form.roles} onChange={(roles) => setForm({ ...form, roles })} variant="permissions" />
                {errors.roles && <small className="error">{errors.roles[0]}</small>}
              </div>
            )}

            {tab === 'nationalities' && (
              <div className="span-2">
                <MultiListSelect
                  listKey="nationality"
                  value={form.nationalities}
                  onChange={(nationalities) => setForm({ ...form, nationalities })}
                />
                {errors.nationalities && <small className="error">{errors.nationalities[0]}</small>}
              </div>
            )}

            {tab === 'languages' && (
              <div className="span-2">
                <MultiListSelect
                  listKey="language"
                  value={form.languages}
                  onChange={(languages) => setForm({ ...form, languages })}
                />
                {errors.languages && <small className="error">{errors.languages[0]}</small>}
              </div>
            )}

            {tab === 'address' && (
              <label className="span-2">
                Address
                <textarea rows={3} value={form.address} onChange={set('address')} />
                {errors.address && <small className="error">{errors.address[0]}</small>}
              </label>
            )}

            <div className="form-actions span-2">
              <Link className="btn btn-ghost" to="/users">
                Cancel
              </Link>
              <button className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}

        {tab === 'identities' &&
          (isNew ? (
            <p className="muted">Save the user first, then you can add identity documents.</p>
          ) : (
            <>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Number</th>
                      <th>Issuer</th>
                      <th>Expiry</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {record?.identities.map((row) => (
                      <tr key={row.id}>
                        <td>{labelFor(lists, 'identity_type', row.identity_type)}</td>
                        <td>{row.number}</td>
                        <td className="muted">{row.issuer ?? '—'}</td>
                        <td className="muted">{row.expiry_date ?? '—'}</td>
                        <td className="right">
                          <IconButton icon={IconTrash} label="Remove" variant="danger-ghost" onClick={() => removeIdentity(row)} />
                        </td>
                      </tr>
                    ))}
                    {record?.identities.length === 0 && (
                      <tr>
                        <td colSpan={5} className="muted center">
                          No identity documents yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <form onSubmit={addIdentity} className="form-grid" style={{ marginTop: 16 }}>
                {identityErrors.general && <div className="alert alert-error span-2">{identityErrors.general[0]}</div>}
                <label>
                  Identity Type *
                  <select
                    value={identityForm.identity_type}
                    onChange={(e) => setIdentityForm({ ...identityForm, identity_type: e.target.value })}
                    required
                  >
                    <option value="">—</option>
                    {listOptions(lists, 'identity_type').map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {identityErrors.identity_type && <small className="error">{identityErrors.identity_type[0]}</small>}
                </label>
                <label>
                  Number *
                  <input
                    value={identityForm.number}
                    onChange={(e) => setIdentityForm({ ...identityForm, number: e.target.value })}
                    required
                  />
                  {identityErrors.number && <small className="error">{identityErrors.number[0]}</small>}
                </label>
                <label>
                  Issuer
                  <input
                    value={identityForm.issuer}
                    onChange={(e) => setIdentityForm({ ...identityForm, issuer: e.target.value })}
                  />
                  {identityErrors.issuer && <small className="error">{identityErrors.issuer[0]}</small>}
                </label>
                <label>
                  Expiry Date
                  <input
                    type="date"
                    value={identityForm.expiry_date}
                    onChange={(e) => setIdentityForm({ ...identityForm, expiry_date: e.target.value })}
                  />
                  {identityErrors.expiry_date && <small className="error">{identityErrors.expiry_date[0]}</small>}
                </label>
                <div className="form-actions span-2">
                  <button className="btn btn-primary">Add identity</button>
                </div>
              </form>
            </>
          ))}

        {tab === 'password' &&
          (isNew ? (
            <p className="muted">Save the user first, then you can set their password.</p>
          ) : (
            <form onSubmit={savePassword} className="form-grid">
              {passwordErrors.general && <div className="alert alert-error span-2">{passwordErrors.general[0]}</div>}
              <label>
                New password *
                <input
                  type="password"
                  value={passwordForm.password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                  required
                  minLength={8}
                />
                {passwordErrors.password && <small className="error">{passwordErrors.password[0]}</small>}
              </label>
              <label>
                Confirm new password *
                <input
                  type="password"
                  value={passwordForm.password_confirmation}
                  onChange={(e) => setPasswordForm({ ...passwordForm, password_confirmation: e.target.value })}
                  required
                  minLength={8}
                />
              </label>
              <div className="form-actions span-2">
                {passwordSaved && <span className="muted">Password changed.</span>}
                <button className="btn btn-primary" disabled={passwordSaving}>
                  {passwordSaving ? 'Saving…' : 'Change password'}
                </button>
              </div>
            </form>
          ))}

        {tab === 'security' &&
          (isNew ? (
            <p className="muted">Save the user first, then you can manage security settings.</p>
          ) : (
            <>
              <div className="card-header">
                <h2>Two-factor authentication</h2>
              </div>
              <div className="span-2" style={{ marginBottom: 24 }}>
                {record?.two_factor_enabled ? (
                  <>
                    <p className="muted">
                      Two-factor authentication is <strong>enabled</strong> for this user.
                    </p>
                    <button
                      type="button"
                      className="btn btn-danger-ghost"
                      onClick={disableTwoFactor}
                      disabled={disabling2fa}
                    >
                      {disabling2fa ? 'Disabling…' : 'Disable two-factor authentication'}
                    </button>
                  </>
                ) : (
                  <p className="muted">
                    Two-factor authentication is <strong>disabled</strong> for this user. Only they can enable it,
                    from their own Account &gt; Security tab.
                  </p>
                )}
              </div>

              <div className="card-header">
                <h2>Active sessions</h2>
                <button type="button" className="btn btn-ghost" onClick={revokeAllSessions} disabled={revoking}>
                  Sign out of all sessions
                </button>
              </div>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Signed in</th>
                      <th>Last used</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions?.map((s) => (
                      <tr key={s.id}>
                        <td>{s.name}</td>
                        <td>{formatDateTime(s.created_at)}</td>
                        <td className="muted">{s.last_used_at ? formatDateTime(s.last_used_at) : 'Never'}</td>
                        <td className="right">
                          <IconButton
                            icon={IconTrash}
                            label="Sign out"
                            variant="danger-ghost"
                            onClick={() => revokeSession(s)}
                          />
                        </td>
                      </tr>
                    ))}
                    {sessions?.length === 0 && (
                      <tr>
                        <td colSpan={4} className="muted center">
                          No active sessions.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ))}

        {tab === 'notifications' &&
          (isNew ? (
            <p className="muted">Save the user first, then you can manage notification settings.</p>
          ) : (
            <div className="form-grid">
              <label className="span-2 checkbox-label">
                <input
                  type="checkbox"
                  checked={record?.notify_security_emails ?? false}
                  onChange={toggleNotify}
                  disabled={notifySaving}
                />
                Email this user when their password is changed
              </label>
              <small className="muted span-2">Security notifications are sent to the user's account email.</small>
            </div>
          ))}

        {tab === 'login-history' &&
          (isNew ? (
            <p className="muted">Save the user first, then you can view their login history.</p>
          ) : (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>IP Address</th>
                    <th>Device</th>
                  </tr>
                </thead>
                <tbody>
                  {history?.data.map((h) => (
                    <tr key={h.id}>
                      <td>{formatDateTime(h.created_at)}</td>
                      <td className="muted">{h.ip_address ?? '—'}</td>
                      <td className="muted">{h.user_agent ?? '—'}</td>
                    </tr>
                  ))}
                  {history && history.data.length === 0 && (
                    <tr>
                      <td colSpan={3} className="muted center">
                        No login history yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <Pagination meta={history} onPage={setHistoryPage} />
            </div>
          ))}
      </div>
    </>
  );
}
